<template>
    <div>
        <!-- Modal dialog content (login message form) -->
        <modal :id="modalId" :title="title">
            <div v-if="error">
                <div class="alert alert-danger">
                    {{ error }}
                </div>
            </div>

            <!-- Content -->
            <div class="form-group row">
              <label for="content" class="col-sm-2 col-form-label">Message text</label>
              <div class="col-sm-10">

                <QuillEditor v-model:content="fields.content" content-type="html" theme="snow" />
                <div class="clearfix"></div>

                <div v-if="errors && errors.content" class="text-danger">{{ errors.content[0] }}</div>
              </div>
            </div>

            <div class="form-group row">
              <label for="style" class="col-sm-2 col-form-label">Message Style</label>
              <div class="col-sm-10">

                <div class="btn-group btn-group-justified">
                  <a v-for="style in styles"
                     @click="selectStyle(style)"
                     :class="'d-table-cell btn btn-alert-' + style + ((fields.style === style) ? ' active' : '')"
                  >{{ style }}</a>
                </div>
              </div>
            </div>

            <template #footer>
                <button type="button" class="btn btn-default" @click="$modal.hide(modalId);">Close</button>
                <button type="submit" class="btn btn-primary" @click="submit()" :disabled="formIsProcessing"><i class="fa fa-fw fa-save"></i>Save</button>
            </template>

        </modal>

    </div>
</template>

<script>
  import HasModal from "../../mixins/HasModal";

  export default {
    mixins: [HasModal],

    emits: ['created', 'updated'],

    /**
     * Input properties
     */
    props: {

      // Message id
      messageId: {
        type: Number,
        default: 0
      },

      // Initial message data.
      initialMessage: {
        type: Object,
        default: () => {return {}}
      },

      // Modal dialog id
      modalId: {
        type: String,
        default: "messageFormModal"
      },

      // Modal dialog title
      title: {
        type: String,
        default: "Add message"
      },

      // Message list component reference.
      reloadMessageListRef: {
        type: String,
        default: 'message_list'
      },

      // Available message styles.
      styles: {
        type: Array,
        default: () => {return []}
      },
    },

    /**
     * Variables (models)
     */
    data() {
      return {
        // Current message object
        message: {},

        // Form fields
        fields: {},

        // Form validation error messages
        errors: {},

        // Internal validation state
        isValid: true,

        // Common error message
        error: '',

        // Form is processing now indicator.
        formIsProcessing: false,
      }
    },

    /**
     * Computed values
     */
    computed: {
      // Is form id "edit" mode
      isEdit() {
        return this.messageId !== 0;
      },
    },

    /**
     * Functions (methods)
     */
    methods: {
      /**
       * Init form
       */
      init() {
        this.clearForm();
        this.message = _.cloneDeep(this.initialMessage);
        this.fields = Object.assign({}, this.message);
        if (!this.fields.style) this.selectStyle('info');
      },

      /**
       * Select message style
       */
      selectStyle(style) {
        this.fields = Object.assign({}, this.fields, { style: style })
      },

      /**
       * Submit form data
       */
      submit() {
        this.validate();
        if (!this.isValid) return;
        if (this.isEdit) {
          this.updateMessage();
        } else {
          this.storeMessage();
        }
      },

      /**
       * Create message
       */
      storeMessage() {
        let formData = this.formData();
        this.formIsProcessing = true;
        axios.post('/admin/system-utilities/login-banner-tool/', formData)
          .then(response => {
            this.hideModal();
            this.$root.$refs[this.reloadMessageListRef].loadMessages();
            this.$emit('created', response.data);
            this.init();
          })
          .catch(error => {
            this.handleErrors(error, 'Unable to save data')
          })
          .finally(() => {
            this.formIsProcessing = false;
          });
      },

      /**
       * Save message
       */
      updateMessage() {
        let formData = this.formData();
        formData.append('_method', "PATCH");
        this.formIsProcessing = true;
        axios.post('/admin/system-utilities/login-banner-tool/' + this.messageId, formData)
          .then(response => {
            this.hideModal();
            this.$root.$refs[this.reloadMessageListRef].loadMessages();
            this.$emit('updated', response.data);
          })
          .catch(error => {
            this.handleErrors(error, 'Unable to save data')
          })
          .finally(() => {
            this.formIsProcessing = false;
          });
      },

      /**
       * Prepare form data
       */
      formData() {
        const formData = new FormData();
        Object.keys(this.fields).forEach(fieldName => {
          const value = this.fields[fieldName] ? this.fields[fieldName] : '';
          formData.append(fieldName, value);
        });

        /*console.group('FORM DATA');
        for (var pair of formData.entries()) {
            console.log(pair[0]+ ', ' + pair[1]);
        }
        console.groupEnd();*/

        return formData;
      },

      /**
       * Validate form
       */
      validate() {
        this.errors = {};
        this.isValid = true;

        // Validate content.
        if (!this.fields.content) {
          this.setError('content', 'Message text must be specified');
        }
      },

      /**
       * Set validation error
       */
      setError(field, message) {
        if (!this.errors[field]) this.errors[field] = [];
        this.errors[field].push(message);
        this.isValid = false;
      },

      /**
       * Handle axios errors
       *
       * @param errorObj
       * @param defaultMessage
       */
      handleErrors(errorObj, defaultMessage) {
        // console.log(errorObj);
        if (errorObj.response.status === 422) {
          this.errors = errorObj.response.data.errors || {};
        } else {
          if (errorObj.response.status === 403) {
            this.error = defaultMessage + ': access denied.';
          } else {
            this.error = defaultMessage;
          }
        }
      },

      /**
       * Clear form fields and messages
       */
      clearForm() {
        this.fields = {};
        this.errors = {};
        this.error = '';
      }
    },

    /**
     * Initialize component on mount.
     */
    mounted() {
      this.init();
    },

  }
</script>

<style scoped>
.btn-alert-info {
  color: #0c5460;
  background-color: #d1ecf1;
}
.btn-alert-info:hover,
.btn-alert-info:focus,
.btn-alert-info.focus {
  background-color: #b1e1ea;
}
.btn-alert-info:not(:disabled):not(.disabled):active,
.btn-alert-info:not(:disabled):not(.disabled).active {
  color: #0c5460;
  background-color: #b1e1ea;
}

.btn-alert-success {
  color: #155724;
  background-color: #d4edda;
}
.btn-alert-success:hover,
.btn-alert-success:focus,
.btn-alert-success.focus {
  background-color: #c3e7cb;
}
.btn-alert-success:not(:disabled):not(.disabled):active,
.btn-alert-success:not(:disabled):not(.disabled).active {
  color: #155724;
  background-color: #c3e7cb;
}

.btn-alert-primary {
  color: #0f4567;
  background-color: #d2e6f4;
}
.btn-alert-primary:hover,
.btn-alert-primary:focus,
.btn-alert-primary.focus {
  background-color: #bad8ec;
}
.btn-alert-primary:not(:disabled):not(.disabled):active,
.btn-alert-primary:not(:disabled):not(.disabled).active {
  color: #0f4567;
  background-color: #bad8ec;
}

.btn-alert-danger {
  color: #7b2c35;
  background-color: #fbdde0;
}
.btn-alert-danger:hover,
.btn-alert-danger:focus,
.btn-alert-danger.focus {
  background-color: #f3c4c9;
}
.btn-alert-danger:not(:disabled):not(.disabled):active,
.btn-alert-danger:not(:disabled):not(.disabled).active {
  color: #7b2c35;
  background-color: #f3c4c9;
}

.btn-alert-warning {
  color: #81592e;
  background-color: #feeede;
}
.btn-alert-warning:hover,
.btn-alert-warning:focus,
.btn-alert-warning.focus {
  background-color: #f3dcc5;
}
.btn-alert-warning:not(:disabled):not(.disabled):active,
.btn-alert-warning:not(:disabled):not(.disabled).active {
  color: #81592e;
  background-color: #f3dcc5;
}
</style>
